import {
  React,
  lazy,
  Suspense,
  useEffect,
  useCallback,
  useRef
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import routes from './routes';
import {
  masterData,
  hospitalLists,
  masterDoctorPrefix,
  getCompanyByCode,
  getMasterWhitelabel
} from './actions';
import Loading from './components/Loading';
import './App.scss';
import 'react-toastify/dist/ReactToastify.css';
import 'react-phone-input-2/lib/style.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toggle/style.css';
import SignalConnect from './SignalConnect';
import InitialComponent from './components/InitialComponent';
import WhiteLabelConfig from './configs/whitelabel';
import { getFileUrl } from './helpers/utilities';

const Landing = lazy(() => import('./views/pages/Landing'));
const Telemed = lazy(() => import('./views/pages/Telemed'));
const TelemedDoctor = lazy(() => import('./views/pages/TelemedDoctor'));
const Authtoken = lazy(() => import('./views/pages/Authtoken'));
const AppLayout = lazy(() => import('./layouts/AppLayout'));
const VideoLayout = lazy(() => import('./layouts/VideoLayout'));
const NotFound = lazy(() => import('./views/errors/NotFound'));
const Version = lazy(() => import('./views/pages/Version'));
let toastifyPosition = 'top-right';
const isLocalEnv = process.env.REACT_APP_ENV === 'local';

const MasterDataLoader = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const hasLoadedFullData = useRef(false);
  const hasLoadedDoctorPrefix = useRef(false);
  const needsFullMasterData = [
    '/doctor/register',
    '/profile',
    '/setting',
    '/closeAccount'
  ].some(path => pathname === path || pathname.startsWith(`${path}/`));

  useEffect(() => {
    if (needsFullMasterData) {
      if (!hasLoadedFullData.current) {
        hasLoadedFullData.current = true;
        dispatch(masterData());
        dispatch(hospitalLists());
      }
    } else if (!hasLoadedDoctorPrefix.current) {
      // Dashboard, consultation and Telemed only need the prefix to render a
      // doctor's display name. Do not request the all-master aggregate.
      hasLoadedDoctorPrefix.current = true;
      dispatch(masterDoctorPrefix());
    }
  }, [dispatch, needsFullMasterData]);

  return null;
};

const App = () => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const loading = useSelector(state => state.loading);

  const isLoading = () => {
    if (loading.status) return true;
    if (_.filter(_.values(loading.names), name => name).length > 0) return true;
    return false;
  };

  const settingWhiteLabel = useCallback(dataParams => {
    async function fetchSetData(data) {
      const theme = {
        companyCode: data.companyCode,
        payments: _.has(data.theme, 'payment') && !_.isEmpty(data.theme.payment) ? data.theme.payment : {},
        mainColor: data.theme.mainColor,
        subColor: data.theme.subColor,
        tertiaryColor: _.has(data, 'theme.tertiaryColor') ? data.theme.tertiaryColor : '#C3AB77',
        title: data.name,
        logo: _.has(data, 'theme.marketplace_logo') ? data.theme.marketplace_logo : '',
        appForCompany: _.has(data, 'appForCompany') ? data.appForCompany : {},
        webApplication: _.has(data.theme, 'webApplication') ? data.theme.webApplication : {},
        contacts: {
          lineAdd: _.has(data, 'doctorLineOA') && !_.isEmpty(data.doctorLineOA) ? data.doctorLineOA : '',
          phoneNumber: _.has(data, 'webTelephone') && !_.isEmpty(data.webTelephone) ? data.webTelephone : ''
        },
        logoBrand: _.has(data, 'imageNew') ? data.imageNew : ''
      };
      dispatch({ type: 'WHITELABEL_SETTING_THEME', playload: { data: theme } });
      if (_.has(theme, 'title') && _.has(theme, 'mainColor')) {
        if (_.has(theme, 'logo.objectKey')) {
          const logoBrandUrl = await getFileUrl(theme.logo);
          if (document.getElementById('logo-brand')) {
            document.getElementById('logo-brand').src = logoBrandUrl;
            document.getElementById('logo-brand').alt = theme.title;
          }
          if (document.getElementById('logo-navbar')) {
            document.getElementById('logo-navbar').src = logoBrandUrl;
            document.getElementById('logo-navbar').alt = theme.title;
          }
          if (document.querySelector('link[rel~="icon"]')) {
            document.querySelector('link[rel~="icon"]').href = logoBrandUrl;
          }
          if (document.querySelector("meta[name~='description']")) {
            document.querySelector("meta[name~='description']").content = theme.title;
          }
          if (document.querySelector("meta[name~='author']")) {
            document.querySelector("meta[name~='author']").content = theme.title;
          }
          if (document.querySelector("meta[name~='keyword']")) {
            document.querySelector("meta[name~='keyword']").content = theme.title;
          }
        }
        document.title = theme.title;
        const style = document.createElement('style');
        document.head.appendChild(style);
        let styleCss = `section.footer { background-color: ${theme.mainColor}!important; }`;
        styleCss += `.block-banner { background-color: ${theme.mainColor}!important; }`;
        styleCss += `.sidebar .nav-link.active { background-color: ${theme.mainColor}!important; }`;
        styleCss += `.sidebar .nav-link:hover { background-color: ${theme.mainColor}!important; }`;
        styleCss += `.side-menu .block-profile.active { background-color: ${theme.mainColor}!important; }`;
        styleCss += `.bg-primary { background-color: ${theme.mainColor}!important; }`;
        styleCss += `.block-banner .login-footer { background-color: ${theme.subColor}!important; }`;
        styleCss += `.container.wallet .block-data > .selected .btn.selected { background-color: ${theme.mainColor}!important; }`;
        styleCss += `.container.wallet .block-data > .selected .btn.selected:hover { background-color: ${theme.mainColor}!important; }`;
        styleCss += `.container.wallet .block-data > .selected .btn-icon-column:hover { background-color: ${theme.mainColor}!important; }`;
        styleCss += `.btn-primary, .btn-primary.disabled:hover { background-color: ${theme.mainColor}!important; border-color: ${theme.mainColor}!important; }`;
        styleCss += `.btn-secondary, .btn-secondary.disabled:hover { background-color: ${theme.mainColor} !important; border-color: ${theme.mainColor}!important; }`;
        styleCss += `.btn-outline-secondary, .btn-outline-secondary.disabled:hover { color: ${theme.mainColor} !important; border-color: ${theme.mainColor}!important; }`;
        styleCss += `.btn-white:hover { background-color: ${theme.mainColor}!important; border-color: ${theme.mainColor}!important; }`;
        styleCss += '.loading { background: rgba(0, 0, 0, 0.7)!important; }';
        if (['#1520A6'].includes(theme.subColor)) {
          styleCss += '.navbar-nav a.nav-link{ color: #ffffff!important;}';
        }
        if (['#FDE304'].includes(theme.mainColor)) {
          styleCss += '.custom-dropdown .btn-outline-secondary ,.block-doctor-schedule .btn-secondary, .sidebar .nav-link.active, .login-footer .btn-secondary, .block-banner .lang-icon .btn, section.footer, section.footer a, .btn-primary, .download .btn-secondary, .account .header, .fixed-menu, section.footer .accordion-button { color: #000000!important;}';
        }
        styleCss += '#register-footer{ display: none;}';
        style.innerHTML = styleCss;
      }
    }
    fetchSetData(dataParams);
  }, [dispatch]);

  useEffect(() => {
    if (/Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      toastifyPosition = 'top-center';
    }
  }, [navigator]);

  useEffect(() => {
    async function fetchData() {
      const whiteLabelMaster = await getMasterWhitelabel();
      if (_.has(whiteLabelMaster, 'data')) {
        WhiteLabelConfig.mapping = _.unionBy(
          whiteLabelMaster.data,
          WhiteLabelConfig.mapping,
          'companyCode'
        );
      }
      const currentDomain = window.location.hostname;
      const whiteLabelData = WhiteLabelConfig.mapping.find(filter => filter.domainName === currentDomain);
      if (_.has(whiteLabelData, 'companyCode')) {
        const style = document.createElement('style');
        document.head.appendChild(style);
        const styleCss = '.loading { background: rgba(0, 0, 0, 1.0)!important; }';
        style.innerHTML = styleCss;
        const response = await getCompanyByCode(whiteLabelData.companyCode);
        if (_.has(response, 'data') && _.isArray(response.data) && response.data.length > 0) {
          settingWhiteLabel(response.data[0]);
          localStorage.setItem('company-code', response.data[0].companyCode);
          dispatch({ type: 'WHITELABEL_SETTING', playload: { data: response.data[0] } });
        }
      } else {
        localStorage.setItem('company-code', '');
      }
    }
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    const useLang = localStorage.getItem('i18nextLng') === null || localStorage.getItem('i18nextLng').indexOf('-') !== -1 ? 'th' : localStorage.getItem('i18nextLng');
    moment.locale(useLang);
  }, [i18n]);

  return (
    <>
      {(isLoading()) && (<Loading message={loading.message} />)}
      <ToastContainer
        autoClose={isLocalEnv ? false : 5000}
        hideProgressBar
        draggable={false}
        position={toastifyPosition}
      />
      <SignalConnect />

      <BrowserRouter>
        <MasterDataLoader />
        <InitialComponent />
        <Routes>
          <Route index path="/login" element={<Landing />} />
          <Route path="/telemed/:token" element={(<Suspense fallback={null}><VideoLayout /></Suspense>)}>
            <Route index element={<Telemed />} />
          </Route>
          <Route path="/telemed/doctor/:token" element={(<Suspense fallback={null}><VideoLayout /></Suspense>)}>
            <Route index element={<TelemedDoctor />} />
          </Route>
          <Route path="/authtoken/:token" element={<Suspense fallback={null}><VideoLayout /></Suspense>}>
            <Route index element={<Authtoken />} />
          </Route>
          <Route index path="/version" element={<Version />} />
          <Route path="/" element={(<Suspense fallback={null}><AppLayout /></Suspense>)}>
            {routes.map(({ path, Element }) => (
              <Route
                key={path}
                path={path}
                element={
                  <Element />
                }
              />
            ))}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </>
  );
};

export default App;
